import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    alpha,
} from '@mui/material';
import {
    LocalShipping as LocalShippingIcon,
    LocationOn as LocationOnIcon,
    FmdGood as FmdGoodIcon,
    ReceiptLong as ReceiptIcon,
    Payment as PaymentIcon,
    FitnessCenter as WeightIcon,
    Straighten as DimensionsIcon,
    Add as AddIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import ShipmentBoxesList from '../../../components/ShipmentBoxesList';
import { cloneDeep, isEmpty, map, mapValues, omit, omitBy, set } from 'lodash';
import { SHIPMENT_BOXS_LIST_SCHEMA, SHIPMENT_PRICE_CALCULATOR_SCHEMA } from '../../../validationSchemas';
import { runJoiValidate } from 'src/_utils';
import { MapitShipCitiesAutocomplete } from 'src/_modules/MapitShip/components';
import { useTranslation } from 'react-i18next';
import Joi from 'joi';
import i18n from 'src/i18n';
import { MapItLocalizedNumberTextField } from 'src/components/common';

// --- Reusable sub-components ---

function DetailItem({ icon, label, value, compact = false }) {
    const theme = useTheme();
    return (
        <Stack
            sx={{
                px: compact ? 1.5 : 2,
                py: compact ? 1 : 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                minWidth: 0,
                gap: 0.5,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={compact ? 0.75 : 1}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Typography
                    variant="caption"
                    sx={{ color: 'text.lighter', fontWeight: 600, lineHeight: 1.2 }}
                    noWrap
                >
                    {label}
                </Typography>
            </Stack>
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: 'text.dark', lineHeight: 1.3 }}
            >
                {value || '—'}
            </Typography>
        </Stack>
    );
}

function SectionHeader({ title }) {
    return (
        <Typography
            variant="body2"
            sx={{
                fontWeight: 700,
                color: 'text.lighter',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mb: 1.5,
            }}
        >
            {title}
        </Typography>
    );
}

// --- Main component ---

export default function OrderShipmentDetailsCard({
    order,
    isEditing,
    isEditingChangeHandler,
    onSaveShipment,
    loading,
    closeEditCards,
    showRadio = false,
    selected = false,
    onSelect = () => { },
    priceCalculatorMode = false,
    lastItem = false,
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // ====== Amount payable on delivery (COD) ======
    const [acceptCod, setAcceptCod] = useState('NO');
    const [amountPayable, setAmountPayable] = useState('0');

    // ======== Handle shipment =======
    const [tempOrder, setTempOrder] = useState({
        origin: '',
        destination: '',
        boxes: [{ dimensions: { length: '', width: '', height: '' }, weight: 1 }],
    });

    useEffect(() => {
        if (!!order) {
            setTempOrder((prev) => ({
                ...prev,
                orderId: order.id,
                boxes: order.otoIntegration?.boxes,
                origin: order.warehouse?.address?.city,
                destination: order.user?.address?.city,
            }));
        } else if (priceCalculatorMode) {
            setTempOrder({
                origin: 'Riyadh',
                destination: 'Riyadh',
                boxes: [{ dimensions: { length: '10', width: '10', height: '10' }, weight: 1 }],
            });
        }
    }, [order]);

    const handleChange = ({ target: { name, value } }) => {
        setTempOrder((prev) => ({ ...prev, [name]: value }));
    };

    const addNewBox = () => {
        setTempOrder((prev) => ({
            ...prev,
            boxes: [...prev?.boxes, { dimensions: { length: '', width: '', height: '' }, weight: 1 }],
        }));
    };

    // ======== Calculate virtual shipment price =======
    const [validationErrors, setValidationErrors] = useState(null);

    const onSubmit = () => {
        try {
            setValidationErrors(null);

            let payload = cloneDeep(tempOrder);

            payload.boxes = map(cloneDeep(payload?.boxes), (box) => {
                box = omit(box, ['_id']);
                box.dimensions = mapValues(box.dimensions, Number);
                box.weight = Number(box.weight);
                return box;
            });

            payload = omitBy(payload, (v) => !v || isEmpty(v));

            if (priceCalculatorMode) {
                payload = set(payload, 'amountPayable', acceptCod === 'YES' ? Number(amountPayable) : 0);
            }

            let schema = !!order ? SHIPMENT_BOXS_LIST_SCHEMA : SHIPMENT_PRICE_CALCULATOR_SCHEMA;
            const data = !!order ? payload?.boxes : payload;

            if (priceCalculatorMode && !order) {
                const minValue = acceptCod === 'YES' ? 1 : 0;
                const amountPayableField = Joi.number()
                    .required()
                    .min(minValue)
                    .messages({
                        'any.required': `{#label} ${i18n.t('validationErrors.isRequired')}`,
                        'number.base': `{#label} ${i18n.t('validationErrors.isNumber')}`,
                        'number.min': `{#label} ${i18n.t('validationErrors.minNumber', { length: minValue })}`,
                    })
                    .label(t('COD (Cash On Delivery)'));

                schema = schema.keys({
                    amountPayable: amountPayableField,
                });
            }

            const errors = runJoiValidate(schema, data);

            if (!!errors) {
                setValidationErrors(errors);
                return;
            }

            if (!!order) {
                let orderData = {
                    id: order?.id,
                    payload: {
                        boxes: payload?.boxes,
                        weight: payload?.boxes.reduce((acc, box) => acc + Number(box.weight), 0),
                        packagesCount: payload?.boxes.length,
                    },
                };
                payload = orderData;
            }

            onSaveShipment(payload);
        } catch (error) {
            console.log(error);
        }
    };

    const boxesVolumetricWeight = useMemo(() => {
        return !isEmpty(tempOrder?.boxes)
            ? tempOrder.boxes.reduce(
                (acc, box) => acc + box.dimensions.length * box.dimensions.width * box.dimensions.height,
                0
            ) / 4000
            : 0;
    }, [tempOrder]);

    const boxesWeight = useMemo(() => {
        return !isEmpty(tempOrder?.boxes)
            ? tempOrder?.boxes?.reduce((acc, box) => acc + Number(box.weight), 0)
            : 0;
    }, [tempOrder]);

    const handleSelectOrder = ({ target: { value } }) => {
        if (!value) {
            setTempOrder({
                origin: '',
                destination: '',
                boxes: [{ dimensions: { length: '', width: '', height: '' }, weight: 1 }],
            });
        } else {
            setTempOrder((prev) => ({
                ...prev,
                orderId: value?.id,
                boxes: value?.otoIntegration.boxes,
                origin: value?.warehouse?.name,
                destination: value?.user?.address?.city,
            }));
        }
    };

    // ======== Render ========

    const renderViewMode = () => {
        const details = [];

        const detailGridProps = { item: true, xs: 6, md: true };

        if (!isMobile && !!order?.orderNumber) {
            details.push(
                <Grid {...detailGridProps} key="orderNo">
                    <DetailItem
                        compact
                        icon={<ReceiptIcon fontSize="small" />}
                        label={t('Order No.')}
                        value={order.orderNumber}
                    />
                </Grid>
            );
        }

        details.push(
            <Grid {...detailGridProps} key="origin" sx={{ pl: { xs: '0 !important', sm: '12px !important' } }}>
                <DetailItem
                    compact
                    icon={<LocationOnIcon fontSize="small" />}
                    label={t('Pickup Location')}
                    value={tempOrder?.origin}
                />
            </Grid>
        );

        details.push(
            <Grid {...detailGridProps} key="destination">
                <DetailItem
                    compact
                    icon={<FmdGoodIcon fontSize="small" />}
                    label={t('Destination')}
                    value={tempOrder?.destination}
                />
            </Grid>
        );

        if (!!order?.otoIntegration?.deliveryCompany) {
            details.push(
                <Grid {...detailGridProps} key="payment">
                    <DetailItem
                        compact
                        icon={<PaymentIcon fontSize="small" />}
                        label={t('Payment method')}
                        value={t(order?.otoIntegration?.paymentMethod)}
                    />
                </Grid>
            );
        }

        if (!!order?.otoIntegration?.actualWeight || !!boxesWeight) {
            details.push(
                <Grid {...detailGridProps} key="actualWeight" sx={{ pl: { xs: '0 !important', sm: '12px !important' } }}>
                    <DetailItem
                        compact
                        icon={<WeightIcon fontSize="small" />}
                        label={t('Actual weight')}
                        value={`${order?.otoIntegration?.actualWeight ?? boxesWeight} kg`}
                    />
                </Grid>
            );
        }

        if (!!order?.otoIntegration?.volumetricWeight || !!boxesVolumetricWeight) {
            details.push(
                <Grid {...detailGridProps} key="volWeight">
                    <DetailItem
                        compact
                        icon={<DimensionsIcon fontSize="small" />}
                        label={t('Volumetric weight')}
                        value={`${order?.otoIntegration?.volumetricWeight ?? boxesVolumetricWeight} kg`}
                    />
                </Grid>
            );
        }

        return (
            <CardContent
                onClick={showRadio ? onSelect : undefined}
                sx={{
                    mb: lastItem ? undefined : 1.5,
                    py: 1.5,
                    pl: { xs: 0.5, sm: 2 },
                    '&:last-child': { pb: 1.5 },
                    bgcolor: selected ? alpha(theme.palette.secondary.main, 0.1) : 'inherit',
                    ...(showRadio && { cursor: 'pointer' }),
                }}
            >
                {/* Mobile: radio + edit on top row, details below full width */}
                {isMobile ? (
                    <Stack spacing={1.5}>
                        {(showRadio || priceCalculatorMode || !!order?.orderNumber) && (
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                {showRadio && (
                                    <Radio
                                        checked={selected}
                                        sx={{ p: 0.5 }}
                                    />
                                )}
                                {!!order?.orderNumber && (
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Box sx={{ display: 'flex', color: 'primary.main', flexShrink: 0 }}>
                                            <ReceiptIcon fontSize="small" />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'text.lighter', fontWeight: 600, lineHeight: 1.2 }} noWrap>
                                            {t('Order No.')}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.dark', lineHeight: 1.3 }}>
                                            {order.orderNumber}
                                        </Typography>
                                    </Stack>
                                )}
                                {(selected || priceCalculatorMode) ? (
                                    <Button
                                        onClick={() => isEditingChangeHandler(true)}
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<EditIcon />}
                                        size="small"
                                        sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}
                                    >
                                        {t('Edit')}
                                    </Button>
                                ) : <Box />}
                            </Stack>
                        )}
                        <Grid container spacing={1.5}>
                            {details}
                        </Grid>
                    </Stack>
                ) : (
                    /* Desktop: radio | details grid | edit button inline */
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {showRadio && (
                            <Radio
                                checked={selected}
                                sx={{ p: 0.5, flexShrink: 0 }}
                            />
                        )}
                        <Grid container spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                            {details}
                        </Grid>
                        {(showRadio || priceCalculatorMode) && (
                            <Button
                                onClick={() => isEditingChangeHandler(true)}
                                variant="outlined"
                                color="primary"
                                startIcon={<EditIcon />}
                                size="small"
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    visibility: (selected || priceCalculatorMode) ? 'visible' : 'hidden',
                                }}
                            >
                                {t('Edit')}
                            </Button>
                        )}
                    </Stack>
                )}
            </CardContent>
        );
    };

    const renderEditMode = () => (
        <>
            <CardHeader
                sx={{ pb: 1 }}
                avatar={<LocalShippingIcon sx={{ color: 'primary.main' }} />}
                title={
                    <Typography variant="h6" sx={{ fontSize: { xs: 16, sm: 18 } }}>
                        {t('shipmentDetails')}
                    </Typography>
                }
            // subheader={
            //     <Chip
            //         label={t('Editing')}
            //         size="small"
            //         color="primary"
            //         variant="outlined"
            //         sx={{ mt: 0.5, height: 22, fontSize: 12 }}
            //     />
            // }
            />
            <Divider />

            {/* Origin & Destination */}
            <CardContent>
                <SectionHeader title={t('Route')} />
                <Grid container spacing={2} alignItems="flex-start">
                    {!!tempOrder?.orderId ? (
                        <>
                            <Grid item xs={12} sm={6}>
                                <DetailItem
                                    icon={<LocationOnIcon fontSize="small" />}
                                    label={t('Pickup Location')}
                                    value={tempOrder?.origin}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailItem
                                    icon={<FmdGoodIcon fontSize="small" />}
                                    label={t('Destination')}
                                    value={tempOrder?.destination}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6}>
                                <MapitShipCitiesAutocomplete
                                    label={t('Pickup Location')}
                                    name="origin"
                                    value={tempOrder?.origin}
                                    onChange={handleChange}
                                    disabled={!!tempOrder?.orderId}
                                    error={validationErrors?.origin}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MapitShipCitiesAutocomplete
                                    label={t('Destination')}
                                    name="destination"
                                    value={tempOrder?.destination}
                                    onChange={handleChange}
                                    disabled={!!tempOrder?.orderId}
                                    error={validationErrors?.destination}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </CardContent>

            <Divider sx={{ mx: 2 }} />

            {/* Boxes */}
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <SectionHeader title={t('Boxes')} />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addNewBox}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        {t('Add Box')}
                    </Button>
                </Box>
                <ShipmentBoxesList
                    boxes={tempOrder?.boxes ?? []}
                    onChange={handleChange}
                    errors={validationErrors?.boxes ?? validationErrors}
                    hideSelectBoxes={!tempOrder?.orderId}
                />
            </CardContent>

            {/* COD Section */}
            {priceCalculatorMode && (
                <>
                    <Divider sx={{ mx: 2 }} />
                    <CardContent>
                        <SectionHeader title={t('Cash on Delivery (COD)')} />
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.warning.main, 0.03),
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Stack>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: 'text.dark', mb: 0.5 }}
                                    >
                                        {t('Collect payment from the receiver on delivery (COD) ?')}
                                    </Typography>
                                    <RadioGroup
                                        aria-labelledby="cod-group"
                                        name="accept-cod"
                                        value={acceptCod}
                                        onChange={(e) => setAcceptCod(e.target.value)}
                                        row
                                    >
                                        <FormControlLabel value="NO" control={<Radio size="small" />} label={t('No')} />
                                        <FormControlLabel value="YES" control={<Radio size="small" />} label={t('Yes')} />
                                    </RadioGroup>
                                </Stack>
                                {acceptCod === 'YES' && (
                                    <Box sx={{ width: { xs: '100%', sm: 260 } }}>
                                        <MapItLocalizedNumberTextField
                                            label={t('Cash to be collected')}
                                            name="amountPayable"
                                            value={amountPayable ?? ''}
                                            required={true}
                                            handleChange={(e) => setAmountPayable(e.target.value)}
                                            error={!!validationErrors?.amountPayable}
                                            max={9999999}
                                            helperText={validationErrors?.amountPayable ?? ''}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </CardContent>
                </>
            )}
        </>
    );

    return (
        <Card
            id="price-calculator-card"
            sx={{
                borderRadius: 3,
                boxShadow: selected
                    ? `0 0 0 2px ${theme.palette.primary.main}`
                    : undefined,
                transition: 'box-shadow 0.2s ease',
                overflow: 'visible',
            }}
        >
            {isEditing ? renderEditMode() : renderViewMode()}

            {isEditing && (
                <>
                    <Divider />
                    <CardActions
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2,
                            justifyContent: 'flex-end',
                            gap: 1,
                        }}
                    >
                        {closeEditCards && (
                            <LoadingButton
                                onClick={() => isEditingChangeHandler(false)}
                                variant="outlined"
                                loading={loading}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                {t('Cancel')}
                            </LoadingButton>
                        )}
                        <LoadingButton
                            variant="contained"
                            loading={loading}
                            onClick={onSubmit}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 3,
                            }}
                            id="view-prices-btn"
                        >
                            {t('View prices')}
                        </LoadingButton>
                    </CardActions>
                </>
            )}
        </Card>
    );
}
